import {
  financeImportKindNames,
  importDateFormatNames,
  importFileMaximumBytes,
  importKindNames,
  type FinanceImportKindName,
  type ImportBatchView,
  type ImportTotalView,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export { financeImportKindNames, importDateFormatNames, importKindNames };

/** People imports run from the Athletes page, finance imports from the Billing page. */
export type ImportScope = 'people' | 'finance';

export function importKindsOf(scope: ImportScope): readonly ImportBatchView['kind'][] {
  return scope === 'finance' ? financeImportKindNames : importKindNames;
}

export function isFinanceKind(kind: ImportBatchView['kind']): kind is FinanceImportKindName {
  return financeImportKindNames.some((name) => name === kind);
}

export const importKindLabelKeys: Readonly<Record<ImportBatchView['kind'], TranslationKey>> = {
  athletes: 'imports.kind.athletes',
  guardians: 'imports.kind.guardians',
  memberships: 'imports.kind.memberships',
  billing_accounts: 'imports.kind.billing_accounts',
  billing_assignments: 'imports.kind.billing_assignments',
  opening_balances: 'imports.kind.opening_balances',
  payments: 'imports.kind.payments',
};

export const importTotalLabelKeys: Readonly<Record<ImportTotalView['type'], TranslationKey>> = {
  opening_due: 'imports.totals.opening_due',
  opening_credit: 'imports.totals.opening_credit',
  payment: 'imports.totals.payment',
};

export const importDateFormatLabelKeys: Readonly<
  Record<ImportBatchView['dateFormat'], TranslationKey>
> = {
  iso: 'imports.dateFormat.iso',
  dmy: 'imports.dateFormat.dmy',
  mdy: 'imports.dateFormat.mdy',
};

export const importBatchStatusLabelKeys: Readonly<
  Record<ImportBatchView['status'], TranslationKey>
> = {
  staged: 'imports.batchStatus.staged',
  committed: 'imports.batchStatus.committed',
  cancelled: 'imports.batchStatus.cancelled',
};

export const importRowStatusLabelKeys: Readonly<
  Record<keyof ImportBatchView['counts'], TranslationKey>
> = {
  ready: 'imports.rowStatus.ready',
  error: 'imports.rowStatus.error',
  needs_decision: 'imports.rowStatus.needs_decision',
  already_imported: 'imports.rowStatus.already_imported',
  skipped: 'imports.rowStatus.skipped',
  committed: 'imports.rowStatus.committed',
};

/** Translation key for a row problem code from the API; unknown codes fall back to "invalid". */
export function importProblemKey(code: string): TranslationKey {
  const key = `import.problem.${code}`;
  return isProblemKey(key) ? key : 'import.problem.required';
}

const problemCodes = [
  'required',
  'too_long',
  'invalid_date',
  'date_in_future',
  'invalid_email',
  'invalid_phone',
  'invalid_locale',
  'invalid_relationship',
  'end_before_start',
  'duplicate_in_file',
  'unknown_athlete',
  'athlete_not_active',
  'membership_overlap',
  'invalid_amount',
  'invalid_currency',
  'currency_mismatch',
  'invalid_balance_type',
  'invalid_method',
  'unknown_account',
  'account_closed',
  'period_closed',
  'assignment_overlap',
  'athlete_not_on_account',
] as const;

function isProblemKey(candidate: string): candidate is TranslationKey {
  return problemCodes.some((code) => candidate === `import.problem.${code}`);
}

/** A staged batch can be committed once nothing is wrong or undecided and something will be imported. */
export function canCommitBatch(batch: ImportBatchView): boolean {
  return (
    batch.status === 'staged' &&
    batch.counts.error === 0 &&
    batch.counts.needs_decision === 0 &&
    batch.counts.ready > 0
  );
}

/** The browser refuses oversized files before reading them; the server enforces the same limit. */
export function isFileTooLarge(sizeInBytes: number): boolean {
  return sizeInBytes > importFileMaximumBytes;
}

export function importsBasePath(organizationId: string, scope: ImportScope): string {
  const area = scope === 'finance' ? 'billing/imports' : 'imports';
  return `/api/v1/organizations/${organizationId}/${area}`;
}

export function templatePath(organizationId: string, kind: ImportBatchView['kind']): string {
  const scope = isFinanceKind(kind) ? 'finance' : 'people';
  return `${importsBasePath(organizationId, scope)}/templates/${kind}`;
}
